import { useMemo } from 'react';
import { usePage } from '@inertiajs/react';

import { buildThemeVariables } from '@/lib/site-design/theme';
import { useSiteStylesState } from '@/stores/site-styles-store';
import type { ButtonSettings, ColorSettings, FontSettings, SharedData } from '@/types/shared';

export type PublicDesignPageProps = {
  colors?: Partial<ColorSettings> | null;
  fonts?: FontSettings | null;
  buttons?: ButtonSettings | null;
};

// The one correct way to read site colors as data, regardless of context:
// editor draft edits take priority, falling back to the page's real Inertia props
// (public site and preview both render via the same public page/props pipeline).
export function useEffectiveColors(): Partial<ColorSettings> | null | undefined {
  const { draftColors } = useSiteStylesState();
  return usePublicDesignSettings({ colors: draftColors ?? undefined }).colors;
}

export function usePublicDesignSettings(overrides?: PublicDesignPageProps) {
  const { site, colors, fonts, buttons } = usePage<SharedData & PublicDesignPageProps>().props;

  const effectiveColors = overrides?.colors ?? colors ?? site?.colors;
  const effectiveFonts = overrides?.fonts ?? fonts ?? site?.fonts;
  const effectiveButtons = overrides?.buttons ?? buttons ?? site?.buttons;
  const themeVariables = useMemo(
    () => buildThemeVariables(effectiveColors, effectiveFonts),
    [effectiveColors, effectiveFonts],
  );
  return {
    colors: effectiveColors,
    fonts: effectiveFonts,
    buttons: effectiveButtons,
    themeVariables,
  };
}
